import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Loader2, Plus, Trash2, FileText, CheckCircle, Users, AlertTriangle } from 'lucide-react';

export default function SJAWizard({ onSuccess }) {
    const { t } = useTranslation();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [formData, setFormData] = useState({
        title: '',
        creator_name: '',
        description: '',
        participants: [''],
        risks: [{ activity: '', hazards: '', measures: '' }],
        status: 'Draft'
    });

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleParticipantChange = (index, value) => {
        const newParticipants = [...formData.participants];
        newParticipants[index] = value;
        setFormData({ ...formData, participants: newParticipants });
    };

    const addParticipant = () => {
        setFormData({ ...formData, participants: [...formData.participants, ''] });
    };

    const removeParticipant = (index) => {
        const newParticipants = formData.participants.filter((_, i) => i !== index);
        setFormData({ ...formData, participants: newParticipants });
    };

    const handleRiskChange = (index, field, value) => {
        const newRisks = [...formData.risks];
        newRisks[index][field] = value;
        setFormData({ ...formData, risks: newRisks });
    };

    const addRisk = () => {
        setFormData({ ...formData, risks: [...formData.risks, { activity: '', hazards: '', measures: '' }] });
    };

    const removeRisk = (index) => {
        const newRisks = formData.risks.filter((_, i) => i !== index);
        setFormData({ ...formData, risks: newRisks });
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch('http://localhost:3001/api/sja/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });
            
            if (!response.ok) throw new Error('Failed to create SJA');
            
            const data = await response.json();
            console.log("SJA Created:", data);
            if (onSuccess) onSuccess();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card className="w-full max-w-lg mx-auto">
            <CardHeader>
                <div className="flex justify-between items-center">
                    <CardTitle className="text-xl font-bold">{t('sja_title', 'Safe Job Analysis (SJA)')}</CardTitle>
                    <span className="text-sm font-medium text-gray-500">{t('step', 'Step')} {step} {t('of', 'of')} 3</span>
                </div>
                
                {/* Progress Bar */}
                <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${(step / 3) * 100}%` }}></div>
                </div>
            </CardHeader>

            <CardContent>
                {step === 1 && (
                    <div className="space-y-4">
                        <h3 className="font-semibold flex items-center gap-2"><FileText size={18} /> {t('sja_general_info', 'General Info')}</h3>
                        <div>
                            <label className="block text-sm font-medium mb-1">{t('job_name', 'Title / Job Name')}</label>
                            <Input name="title" value={formData.title} onChange={handleChange} placeholder="e.g. Rigging Stage A" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">{t('responsible', 'Responsible (Creator)')}</label>
                            <Input name="creator_name" value={formData.creator_name} onChange={handleChange} placeholder={t('author_placeholder', 'Your Name')} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">{t('description', 'Description')}</label>
                            <Textarea name="description" value={formData.description} onChange={handleChange} className="h-24" placeholder="Briefly describe the job..." />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">{t('participants', 'Participants')}</label>
                            {formData.participants.map((p, i) => (
                                <div key={i} className="flex gap-2 mb-2">
                                    <Input value={p} onChange={(e) => handleParticipantChange(i, e.target.value)} placeholder={`${t('participants', 'Participant')} ${i + 1}`} />
                                    <Button variant="ghost" size="icon" onClick={() => removeParticipant(i)} className="text-red-500">
                                        <Trash2 size={16} />
                                    </Button>
                                </div>
                            ))}
                            <Button variant="outline" size="sm" onClick={addParticipant} className="w-full mt-2">
                                <Plus size={16} className="mr-2" /> {t('add_participant', 'Add Participant')}
                            </Button>
                        </div>

                        <div className="flex justify-end pt-4">
                            <Button onClick={() => setStep(2)} disabled={!formData.title || !formData.creator_name}>{t('next', 'Next')}</Button>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="space-y-4">
                        <h3 className="font-semibold flex items-center gap-2"><AlertTriangle size={18} /> {t('sja_risk_assessment', 'Risk Assessment')}</h3>
                        <p className="text-xs text-gray-500">Identify hazards and measures.</p>

                        {formData.risks.map((risk, i) => (
                            <div key={i} className="p-3 border rounded-lg bg-gray-50 dark:bg-zinc-800 space-y-3 relative group">
                                <Button variant="ghost" size="icon" onClick={() => removeRisk(i)} className="absolute top-2 right-2 text-gray-400 hover:text-red-500">
                                    <Trash2 size={16} />
                                </Button>
                                <div>
                                    <label className="text-xs font-semibold text-gray-500">{t('activity', 'Activity')}</label>
                                    <Input value={risk.activity} onChange={(e) => handleRiskChange(i, 'activity', e.target.value)} placeholder="e.g. Lifting truss" />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-gray-500">{t('hazards', 'Hazards')}</label>
                                    <Input value={risk.hazards} onChange={(e) => handleRiskChange(i, 'hazards', e.target.value)} placeholder="e.g. Falling objects" />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-gray-500">{t('measures', 'Measures')}</label>
                                    <Input value={risk.measures} onChange={(e) => handleRiskChange(i, 'measures', e.target.value)} placeholder="e.g. Hard hats, secure zone" />
                                </div>
                            </div>
                        ))}

                        <Button variant="outline" size="sm" onClick={addRisk} className="w-full">
                            <Plus size={16} className="mr-2" /> {t('add_risk_item', 'Add Risk Item')}
                        </Button>

                        <div className="flex justify-between pt-4">
                            <Button variant="outline" onClick={() => setStep(1)}>{t('back', 'Back')}</Button>
                            <Button onClick={() => setStep(3)}>{t('sja_review_sign', 'Review & Sign')}</Button>
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="space-y-6">
                        <h3 className="font-semibold flex items-center gap-2"><CheckCircle size={18} /> {t('sja_review_sign', 'Review & Sign')}</h3>

                        <div className="bg-gray-50 dark:bg-zinc-800 p-4 rounded-md text-sm space-y-2">
                            <p><strong>{t('job_name', 'Job')}:</strong> {formData.title}</p>
                            <p><strong>{t('responsible', 'Responsible')}:</strong> {formData.creator_name}</p>
                            <p><strong>{t('participants', 'Participants')}:</strong> {formData.participants.length}</p>
                            <p><strong>Risk Items:</strong> {formData.risks.length}</p>
                        </div>

                        <div className="border border-dashed border-gray-300 dark:border-zinc-600 rounded-lg h-32 flex items-center justify-center bg-gray-50 dark:bg-zinc-800 cursor-crosshair">
                            <p className="text-gray-400 text-sm italic">{t('digital_signature', 'Digital Signing Area (Placeholder)')}</p>
                        </div>
                        <p className="text-xs text-center text-gray-500">By clicking submit, you certify this SJA is correct.</p>

                        {error && <p className="text-red-500 text-sm text-center">{error}</p>}

                        <div className="flex justify-between pt-4">
                            <Button variant="outline" onClick={() => setStep(2)}>{t('back', 'Back')}</Button>
                            <Button onClick={handleSubmit} disabled={loading} className="w-32">
                                {loading ? <Loader2 className="animate-spin" size={18} /> : t('submit_sja', 'Submit SJA')}
                            </Button>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}